// DataNomad.js — merged from Service.js (live) + DataNomad.js (new)
//
// Keeps Helmet (react-helmet-async) SEO from Service.js
// Includes all the new sections/components from DataNomad.js
// One shared PricingCard; same look/feel as before.

import React from "react";
import Footer from "./Footer";
import { Link } from "react-router-dom";
import { BackgroundBeams } from "../ui/background-beams.tsx";
import { UnorderedList, ListItem } from "@chakra-ui/react";
import { WobbleCard } from "../ui/WobbleCard";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async"; // ✅ keep SEO (from Service.js)

// (Optional) Heroicons used by the “How to Get Started” section
import {
  AdjustmentsHorizontalIcon,
  UserIcon,
  DocumentTextIcon,
  CreditCardIcon,
} from "@heroicons/react/24/outline";

// --- Small helper to preserve intended line breaks in step descriptions
const LineBreaks = ({ text }) => {
  const lines = String(text).split(/\r?\n/);
  return (
    <>
      {lines.map((line, i) => (
        <React.Fragment key={i}>
          {line}
          {i < lines.length - 1 && <br />}
        </React.Fragment>
      ))}
    </>
  );
};

// --- “How to Get Started” content (from new DataNomad.js)
const features = [
  {
    name: "Step 1: Select Your Tier",
    description:
      "Start by choosing the subscription plan that best fits your organisation’s compliance goals.\n\nEach tier is designed to support different levels of monitoring, from essential regulatory updates to comprehensive coverage with in-depth insights.",
    icon: AdjustmentsHorizontalIcon,
  },
  {
    name: "Step 2: Input Your Details",
    description:
      "Next enter your contact details such as your Name, Surname and Email address.\n\nThis ensures that all communications, alerts, and compliance updates are personalised and delivered straight to your inbox.",
    icon: UserIcon,
  },
  {
    name: "Step 3: Input the Full Name of the Regulations",
    description:
      "Tell us exactly which standards or regulations you want to keep up to date on, either ISO 27001, HIPAA, GDPR, FICA, Labour Regulations etc.\n\nProviding the full names of the regulations helps us tailor the monitoring service to your precise compliance need.",
    icon: DocumentTextIcon,
  },
  {
    name: "Step 4: Select Your Frequency and Checkout",
    description:
      "Finally, decide how often you’d like to receive regulatory updates either, weekly, monthly, or quarterly.\n\nOnce selected, review your choices and securely complete the checkout process to activate your subscription.",
    icon: CreditCardIcon,
  },
];

// --- Shared Pricing Card (consistent with both files)
const PricingCard = ({ tier, price, features, emphasized, onClick }) => {
  const borderColor = "#e500ff";
  const emphasizedHoverColor = "rgba(229, 0, 255, 0.8)";
  const standardHoverColor = "rgba(55, 65, 81, 0.8)";

  return (
    <motion.div
      className="relative rounded-xl shadow-md p-6 text-white/80 transition-transform duration-300"
      style={{
        zIndex: emphasized ? 10 : 1,
        minHeight: "320px",
        border: `2px solid ${borderColor}`,
        backgroundColor: "transparent",
      }}
      onClick={onClick}
      whileHover={{
        y: -5,
        backgroundColor: emphasized ? emphasizedHoverColor : standardHoverColor,
        scale: 1.05,
        zIndex: 20,
      }}
    >
      {emphasized && (
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 -mt-3 text-black text-sm py-1 px-3 rounded-full shadow-md"
          style={{ backgroundColor: "#00ff9f" }}
        >
          Most Popular
        </div>
      )}
      <h3 className="text-xl font-semibold" style={{ color: "#00b8ff" }}>
        {tier}
      </h3>
      <div className="mt-2">
        <span className={`text-3xl font-bold ${emphasized ? "text-white" : "text-gray-300"}`}>
          R{price}
        </span>
        <span className={`text-gray-500 ${emphasized ? "text-gray-400" : ""}`}>/month</span>
      </div>
      <ul className="mt-4 space-y-3">
        {features.map((feature, index) => (
          <li key={index} className={`text-sm ${emphasized ? "text-white" : "text-gray-400"}`}>
            ✅ {feature}
          </li>
        ))}
      </ul>
    </motion.div>
  );
};

const DataNomad = () => {
  return (
    <>
      {/* ✅ Helmet from Service.js for SEO/meta parity with live page */}
      <Helmet>
        <title>Services | Gemini Cybersecurity — DataNomad Compliance Updates</title>
        <meta
          name="description"
          content="See how DataNomad monitors ISO/GDPR/HIPAA updates and delivers clear, actionable insights to your inbox."
        />
        <link rel="canonical" href="https://gemini-cybersecurity.com/service" />
        <meta name="robots" content="index,follow" />
      </Helmet>

      <div className="relative w-screen bg-black text-white">
        {/* Nav */}
        <nav className="fixed top-0 left-0 w-full bg-black py-4 text-center z-50">
          <ul className="flex justify-center space-x-8">
            <li>
              <a href="/" className="text-white hover:text-blue-400 text-lg">
                Home
              </a>
            </li>
            <li>
              <a href="/#contact" className="text-white hover:text-blue-400 text-lg">
                Contact Us
              </a>
            </li>
          </ul>
        </nav>

        <div className="relative w-full">
          {/* Hero */}
          <section
            id="home"
            className="relative min-h-screen flex flex-col justify-center items-center text-center w-full mt-[-50px]"
          >
            <BackgroundBeams />
            <h1 className="text-5xl font-bold z-10">DataNomad</h1>
            <p className="text-lg mt-4 z-10">
              Stay updated with the latest changes to cybersecurity compliance regulations
              and receive actionable insights for your organisation.
            </p>

            {/* CTAs */}
            <div className="flex gap-8 mt-6 z-60">
              <Link to="/subscribe"> {/*</div>target="_blank" rel="noopener noreferrer">*/}
                <button className="relative px-6 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg z-60">
                  Subscribe
                </button>
              </Link>
              <a
                href="https://calendly.com/rowan-gemini-cybersecurity/30min"
                target="_blank"
                rel="noopener noreferrer"
              >
                <button className="relative px-6 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg z-60">
                  Request a Demo
                </button>
              </a>
            </div>
          </section>

          {/* Wobble Cards (from the new DataNomad) */}
          <div className="mt-[-200px] grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            <WobbleCard
              bgColor="bg-gradient-to-br from-blue-400 to-blue-600"
              containerClassName="col-span-2 row-span-1 md:min-h-[300px]"
              imageSrc=""
              alt="DataNomad overview"
            >
              <h2 className="text-xl font-semibold">How We Do it</h2>
              <p className="mt-2 text-white">
                Utilising APIs from vast AI systems, we are able to send you the information you need, straight to your inbox.
              </p>
              <p className="mt-3 text-white">
                DataNomad automates the process of monitoring regulatory standards like ISO/IEC 27001, HIPAA, and GDPR.
                It tracks updates in real-time, analyses what’s changed, and delivers easy-to-understand insights directly
                to your inbox on a weekly, monthly, or quarterly intervals, based on your needs.
              </p>
              <p className="mt-4 text-white">
                Standards and regulations are constantly evolving and missing even a small update could expose your organisation
                to non-compliance risks, reputational damage, or legal penalties. DataNomad ensures you never fall behind,
                with tailored updates that cut through the noise.
              </p>
            </WobbleCard>

            <WobbleCard
              bgColor="bg-gradient-to-br from-green-400 to-emerald-600"
              containerClassName="col-span-2 min-h-[250px]"
              imageSrc=""
            >
              <h2 className="text-xl font-semibold">AI-Powered Insights</h2>
              <p className="mt-2 text-white">Receive updates in real time with actionable steps.</p>
              <p className="mt-2 text-white">
                Instead of manually checking for changes across multiple standards, our system does the heavy lifting.
                <br /> Here are some of the items you’ll receive:
                <UnorderedList mt={2} mb={4}>
                  <ListItem>Actionable AI-powered summaries of regulatory changes.</ListItem>
                  <ListItem>Risk assessments on regulatory changes and implementation guides (Premium tier).</ListItem>
                  <ListItem>Custom notifications based on your selected frequency.</ListItem>
                </UnorderedList>
                This saves your team valuable time, reduces compliance overhead and helps maintain audit readiness year-round.
              </p>
            </WobbleCard>

            <WobbleCard
              bgColor="bg-gradient-to-br from-purple-400 to-purple-600"
              containerClassName="col-span-2 min-h-[200px]"
              imageSrc=""
            >
              <h2 className="text-lg font-semibold">How It Helps Your Organisation</h2>
              <p className="mt-2 text-white">Set how frequently you would like to be updated by DataNomad.</p>
              <p className="mt-2 text-white">
                Whether you’re a startup or an enterprise, staying compliant is non-negotiable. Our platform supports your
                compliance strategy. It’s like having a virtual compliance assistant watching your blind spots, so you can
                focus on growing your business.
              </p>
            </WobbleCard>

            <WobbleCard
              bgColor="bg-pink-600"
              containerClassName="col-span-2 min-h-[300px]"
              imageSrc=""
            >
              <h2 className="text-xl font-semibold">Compliance, Delivered</h2>
              <p className="mt-2 text-white">Maximise your returns with expert-backed strategies.</p>
              <p className="mt-2 text-white">
                There’s already too much noise in your inbox. We cut through it with clear, curated, and personalised updates
                that matter. No more scrambling when audits come up. No more last-minute fixes. Just simple, AI-generated
                guidance that’s actually useful, delivered right on time. Compliance doesn’t have to be a chore. Let us do the
                heavy lifting for you.
              </p>
            </WobbleCard>
          </div>

          {/* GETTING STARTED (from new DataNomad) */}
          <section className="relative mt-20 py-24 sm:py-32 bg-black text-white flex flex-col justify-center items-center text-left w-full">
            <BackgroundBeams />
            <div className="mx-auto max-w-7xl px-6 lg:px-8 z-10">
              <div className="mx-auto max-w-2xl lg:text-center">
                <h2 className="text-4xl sm:text-5xl font-bold text-white">How to Get Started</h2>
                <p className="mt-2 text-2xl font-semibold tracking-tight text-white lg:text-balance">
                  Start receiving <span className="whitespace-nowrap">compliance updates</span> in four simple steps
                </p>
                <p className="mt-6 text-lg/8 text-gray-300">
                  DataNomad monitors your submitted regulations, flags changes, explains the potential impact and risks,
                  recommends actions to stay compliant, and cites every source.
                </p>
              </div>

              <div className="mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-4xl">
                <ul
                  className="grid max-w-xl grid-cols-1 gap-x-10 gap-y-16 lg:max-w-none lg:grid-cols-2 lg:gap-y-8"
                  aria-label="Setup steps"
                >
                  {features.map((feature, idx) => {
                    const cleanTitle = feature.name.replace(/^Step\s*\d+\s*:\s*/i, "");
                    return (
                      <li key={feature.name}>
                        <article className="relative rounded-2xl bg-white/[0.03] ring-1 ring-white/10 p-7 md:p-8 pl-20 transition duration-300 hover:-translate-y-0.5 hover:ring-white/20 focus-within:ring-indigo-400/40">
                          {/* Decorative number */}
                          <div
                            aria-hidden="true"
                            className="absolute -left-3 -top-3 flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-500 text-white font-semibold ring-2 ring-indigo-300/40"
                          >
                            {String(idx + 1).padStart(2, "0")}
                          </div>

                          <h3 className="text-lg md:text-xl font-semibold tracking-tight text-white">
                            <span className="sr-only">Step {idx + 1}: </span>
                            {cleanTitle}
                          </h3>
                          <p className="mt-2 text-base md:text-lg text-gray-300 leading-relaxed">
                            <LineBreaks text={feature.description} />
                          </p>
                        </article>
                      </li>
                    );
                  })}
                </ul>
              </div>

              <p className="mt-6 text-gray-400 text-sm text-center italic">
                * For regulations that cross borders or regions, please also include the applicable region/country
                (e.g., GDPR EU, GDPR UK, Labour Regulations EU, Labour Regulations SA/ZA).
              </p>
            </div>
          </section>

          {/* Pricing */}
          <div className="py-20 mt-8">
            <div className="max-w-6xl mx-auto text-center">
              <h2 className="text-3xl font-bold text-white mb-8">Our Pricing Plans</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <PricingCard
                  tier="Basic"
                  price="150.00"
                  features={["1 Regulation", "Email Support", "Monthly Updates", "Cancel Anytime"]}
                />
                <PricingCard
                  tier="Standard"
                  price="500.00"
                  features={["5 Regulations", "Priority Support", "Monthly or Weekly Updates", "Cancel Anytime"]}
                  emphasized
                />
                <PricingCard
                  tier="Premium"
                  price="750.00"
                  features={[
                    "10 Regulations",
                    "AI Insights + Implementation Guide",
                    "Priority Support",
                    "Monthly or Weekly Updates",
                    "Cancel Anytime",
                  ]}
                />
              </div>
              <div className="mt-8">
                <Link to="/subscribe">
                  <button className="px-4 py-2 font-bold text-white rounded-lg z-60 mt-[-2] !bg-[#00b8ff] hover:!bg-[#5e00ff] transition-colors duration-200">
                    Subscribe
                  </button>
                </Link>
              </div>
              <p className="mt-6 text-gray-400 text-sm">
                * Prices are billed monthly.<br />
                * To cancel your subscription simply send an email to rowan@gemini-cybersecurity.com
              </p>
            </div>
          </div>

          {/* Contact */}
          <section
            id="contact"
            className="relative py-10 bg-black text-white flex flex-col justify-center items-center text-center w-full"
          >
            <BackgroundBeams />
            <h2 className="text-4xl font-bold z-10">Contact Us</h2>
            <p className="fontSize-lg textAlign-center z-10">
              Have questions or want to learn more? Reach out to our team.
            </p>
            <div className="mt-[-2] z-10 flex flex-col items-center">
              <p className="text-lg">
                Email:{" "}
                <a href="mailto:rowan@gemini-cybersecurity.com" className="cyan-link">
                  rowan@gemini-cybersecurity.com
                </a>
              </p>
              <p className="text-md mt-[-2]">OR</p>
              <a
                href="https://calendly.com/rowan-gemini-cybersecurity/30min"
                target="_blank"
                rel="noopener noreferrer"
              >
                <button className="relative px-2 py-2 bg-purple-500 hover:bg-purple-600 font-bold text-white rounded-lg z-60 mt-[-2]">
                  Request Demo
                </button>
              </a>
            </div>
          </section>

          <Footer />
        </div>
      </div>
    </>
  );
};

export default DataNomad;
